import type { RosterDomainKey, RosterRow } from './rosterRows';

export type CategoricalDimension =
  | 'school'
  | 'grade'
  | 'teacher'
  | 'elStatus'
  | 'cohort'
  | 'dataQuality'
  | 'planSignoff';

export const MISSING_SCORE_PREFIX = 'missing_';

export type RangeDimension = 'compositePL' | 'domainPL' | 'yearsInEl';

export type CategoricalChip = {
  dimension: CategoricalDimension;
  values: string[];
};

type Bounds = { from?: number; to?: number };

export type RangeChip =
  | ({ dimension: Exclude<RangeDimension, 'domainPL'>; domain?: never } & Bounds)
  | ({ dimension: 'domainPL'; domain: RosterDomainKey } & Bounds);

export type FilterChip = CategoricalChip | RangeChip;

export const EPSILON = 1e-9;

function isCategorical(chip: FilterChip): chip is CategoricalChip {
  return 'values' in chip;
}

export function categoricalValues(row: RosterRow, dimension: CategoricalDimension): string[] {
  switch (dimension) {
    case 'school':
      return [row.school];
    case 'grade':
      return [row.grade];
    case 'teacher':
      return row.teachers;
    case 'elStatus':
      return [row.elStatus];
    case 'cohort':
      return row.cohorts;
    case 'dataQuality':
      return row.missingDomains.map((domain) => `${MISSING_SCORE_PREFIX}${domain}`);
    case 'planSignoff':
      return row.planSignOff ? [row.planSignOff.value] : [];
  }
}

function rangeValue(row: RosterRow, chip: RangeChip): number | null {
  switch (chip.dimension) {
    case 'compositePL':
      return row.compositePL;
    case 'yearsInEl':
      return row.yearsInEl;
    case 'domainPL':
      return chip.domain == null ? null : row.domainPLs[chip.domain];
  }
}

export function isEvaluable(chip: RangeChip): boolean {
  if (chip.from == null && chip.to == null) return false;
  return chip.dimension !== 'domainPL' || chip.domain != null;
}

function matches(row: RosterRow, chip: FilterChip): boolean {
  if (isCategorical(chip)) {
    const values = categoricalValues(row, chip.dimension);
    return chip.values.some((value) => values.includes(value));
  }
  if (!isEvaluable(chip)) return true;
  const value = rangeValue(row, chip);
  if (value == null) return false;
  if (chip.from != null && value < chip.from - EPSILON) return false;
  if (chip.to != null && value > chip.to + EPSILON) return false;
  return true;
}

export function applyFilters(rows: RosterRow[], chips: FilterChip[]): RosterRow[] {
  if (chips.length === 0) return rows;
  return rows.filter((row) => chips.every((chip) => matches(row, chip)));
}
